import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dispatchAPI, ordersAPI, printingAPI, volumeUnitsAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import { cn, formatNumber } from '../lib/utils';
import { referenceLisible } from '../lib/libelles';
import { allocationBlockReason, clampAllocation, progressOf, suggestSplit } from '../lib/orderProgress';
import {
  UNIT_CISTERNS,
  UNIT_LITERS,
  cisternLabelOf,
  cisternLitersOf,
} from '../lib/volumeUnits';
import { PackageCheck } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import { PrintMenu } from '../components/business/PrintMenu';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';
import { useEstMobile } from '../hooks/useEstMobile';
import { DispatchOrderList } from '../components/dispatch/DispatchOrderList';
import { DispatchPanel } from '../components/dispatch/DispatchPanel';
import { libre, messageErreur, nombreEntier } from '../components/dispatch/outils';
import StationsToResupply from '../components/stations/supply/StationsToResupply';

// Deux vues de la même liste: ce qu'il reste à faire, ou tout ce qui est en
// cours (affectées et en route comprises), pour voir où en est chaque commande.
const VUE_A_AFFECTER = 'A_AFFECTER';
const VUE_EN_COURS = 'EN_COURS';

// Maître-détail dès 1280 px: le panneau est fixe à droite. En dessous, le même
// contenu s'ouvre dans un volet.
const REQUETE_LARGE = '(min-width: 1280px)';
const lireLarge = () => {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(REQUETE_LARGE).matches
      : true;
  } catch {
    return true;
  }
};
const useEcranLarge = () => {
  const [large, setLarge] = useState(lireLarge);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const requete = window.matchMedia(REQUETE_LARGE);
    const suivre = () => setLarge(requete.matches);
    suivre();
    if (requete.addEventListener) requete.addEventListener('change', suivre);
    else if (requete.addListener) requete.addListener(suivre);
    return () => {
      if (requete.removeEventListener) requete.removeEventListener('change', suivre);
      else if (requete.removeListener) requete.removeListener(suivre);
    };
  }, []);
  return large;
};

// Hauteur du panneau fixe: toute la fenêtre une fois collé en haut, et
// seulement ce qui reste sous l'en-tête tant que la page n'a pas défilé, pour
// que la barre d'actions du bas soit toujours visible. Mêmes réserves que les
// colonnes fixes des autres écrans (`ColonneFixe`): 5 rem en haut (barre du
// haut + air) et 5 rem en bas (pied de page + marge), faute de quoi, en bout
// de page, le pied remonte le panneau sous la barre et cache son en-tête.
const HAUT_PANNEAU = 80;
const BAS_PANNEAU = 80;
const useHauteurPanneau = (actif) => {
  const reperes = useRef(null);
  const [hauteur, setHauteur] = useState(null);
  useEffect(() => {
    if (!actif || typeof window === 'undefined') return undefined;
    let image = 0;
    const calculer = () => {
      image = 0;
      const haut = reperes.current?.getBoundingClientRect().top;
      if (typeof haut !== 'number') return;
      const libre = window.innerHeight - Math.max(haut, HAUT_PANNEAU) - BAS_PANNEAU;
      setHauteur(Math.max(360, Math.round(libre)));
    };
    const planifier = () => {
      if (!image) image = window.requestAnimationFrame(calculer);
    };
    calculer();
    window.addEventListener('scroll', planifier, { passive: true, capture: true });
    window.addEventListener('resize', planifier);
    return () => {
      if (image) window.cancelAnimationFrame(image);
      window.removeEventListener('scroll', planifier, { capture: true });
      window.removeEventListener('resize', planifier);
    };
  }, [actif]);
  return [reperes, hauteur];
};

const DispatchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const large = useEcranLarge();
  const mobile = useEstMobile();
  const [reperesPanneau, hauteurPanneau] = useHauteurPanneau(large);
  // Sous 1280 px le détail s'ouvre dans un volet, au clic sur une commande.
  const [volet, setVolet] = useState(Boolean(searchParams.get('commande')));
  const [rechercheCommande, setRechercheCommande] = useState('');
  const [orders, setOrders] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [vue, setVue] = useState(VUE_A_AFFECTER);
  const [selectedId, setSelectedId] = useState(searchParams.get('commande') || null);
  const [availableTrucks, setAvailableTrucks] = useState([]);
  const [trucksLoading, setTrucksLoading] = useState(false);
  const [dispatchReadiness, setDispatchReadiness] = useState(null);
  // Les quantités saisies, par trajet. Vide = « je n'ai rien saisi »: la case
  // montre alors la quantité proposée en grisé.
  const [quantities, setQuantities] = useState({});
  // Unité de lecture, purement d'affichage: les envois restent en litres.
  const [displayUnit, setDisplayUnit] = useState(UNIT_LITERS);
  const [volumeSettings, setVolumeSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Un second envoi par-dessus le premier reste interdit: le serveur validerait
  // le deuxième contre un reste que le premier n'a pas encore réduit.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Le dernier choix de commande gagne: une réponse lente d'une commande déjà
  // quittée ne doit pas écraser les camions de la commande courante.
  const selectionToken = useRef(0);

  useEffect(() => {
    Promise.resolve(volumeUnitsAPI.get())
      .then(setVolumeSettings)
      .catch(() => setVolumeSettings(null));
  }, []);

  const loadOrders = useCallback(async ({ silencieux = false } = {}) => {
    if (!silencieux) setIsLoading(true);
    try {
      // Toutes les pages: avec 200 commandes seulement, la deux-cent-unième
      // n'existait pas pour l'écran, et le total n'était jamais vérifié.
      const result = await fetchAllPages((page) => ordersAPI.getAllPaged(page), {
        pageSize: 200,
      });
      const items = result.items || [];
      setOrders(items);
      setLoadError(false);
      return items;
    } catch (error) {
      console.error('Impossible de charger les commandes', error);
      setLoadError(true);
      toast({
        title: 'Chargement impossible',
        description: 'Les commandes n’ont pas pu être chargées. Réessayez dans un instant.',
        variant: 'destructive',
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['commandes', 'repartition', 'camions', 'chauffeurs', 'trajets', 'convois'], loadOrders);


  // « À affecter » = il reste des litres sans camion. C'est le serveur qui le
  // dit (`progress.to_allocate_liters`): l'écran ne refait plus le calcul, et ne
  // soustrait donc plus deux fois le livré.
  const activeOrders = useMemo(
    () => orders.filter((order) => progressOf(order).to_allocate_liters > 0),
    [orders],
  );
  // En cours = ni livrée, ni annulée: affectées et en route comprises.
  const openOrders = useMemo(
    () =>
      orders.filter((order) => {
        const { state } = progressOf(order);
        return state !== 'LIVREE' && state !== 'ANNULEE';
      }),
    [orders],
  );
  const vueOrders = vue === VUE_A_AFFECTER ? activeOrders : openOrders;
  const shownOrders = useMemo(() => {
    const terme = rechercheCommande.trim().toLowerCase();
    if (!terme) return vueOrders;
    return vueOrders.filter((order) =>
      `${order.reference_number || ''} ${order.client_name || ''} ${order.delivery_location || ''}`
        .toLowerCase()
        .includes(terme),
    );
  }, [vueOrders, rechercheCommande]);

  // Des centaines de commandes: la liste se lit par pages (20, 50, 100), la
  // recherche et la vue ramènent à la première.
  const pageCommandes = usePagination(shownOrders, {
    cle: 'commandes',
    reinitialiser: `${vue}|${rechercheCommande}`,
  });

  const totalToAllocate = activeOrders.reduce(
    (sum, order) => sum + progressOf(order).to_allocate_liters,
    0,
  );

  const selectedOrder = useMemo(
    () => orders.find((order) => order.id === selectedId) || null,
    [orders, selectedId],
  );
  const selectedProgress = selectedOrder ? progressOf(selectedOrder) : null;

  const loadTrucks = useCallback(async (order) => {
    const token = ++selectionToken.current;
    setTrucksLoading(true);
    try {
      const [trucks, readiness] = await Promise.all([
        ordersAPI.getAvailableTrucks(order.id),
        Promise.resolve(ordersAPI.getDispatchReadiness(order.id)).catch(() => null),
      ]);
      if (token !== selectionToken.current) return;
      setDispatchReadiness(readiness);
      // Le serveur les renvoie du plus gros volume au plus petit: c'est l'ordre
      // de la répartition automatique, on ne le retrie pas.
      setAvailableTrucks(trucks || []);
    } catch (error) {
      if (token !== selectionToken.current) return;
      console.error('Impossible de charger les camions', error);
      toast({
        title: 'Chargement impossible',
        description: messageErreur(error, 'Les camions libres n’ont pas pu être chargés.'),
        variant: 'destructive',
      });
    } finally {
      if (token === selectionToken.current) setTrucksLoading(false);
    }
  }, []);

  // La sélection vit dans l'adresse (?commande=...): un lien, un rechargement
  // ou le bouton retour retrouvent la même commande.
  // La commande dont les camions sont chargés ou en cours de chargement.
  const chargePour = useRef(null);
  const handleSelectOrder = useCallback(
    (order, { clavier = false, ouvrirVolet = true } = {}) => {
      chargePour.current = order.id;
      setSelectedId(order.id);
      setQuantities({});
      setAvailableTrucks([]);
      setDispatchReadiness(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('commande', order.id);
          // Les camions de la commande précédente n'ont pas de « page 3 » ici.
          next.delete('camions-page');
          return next;
        },
        { replace: true },
      );
      if (!clavier && ouvrirVolet) setVolet(true);
      loadTrucks(order);
    },
    [loadTrucks, setSearchParams],
  );

  // Après une affectation: relire la liste (reste, état) ET les camions de la
  // commande ouverte. La commande reste ouverte même quand elle n'a plus rien
  // à affecter: l'opérateur voit le résultat au lieu de la voir disparaître.
  const refreshAfterAllocation = useCallback(
    async (orderId) => {
      const items = await loadOrders();
      const fresh = items.find((item) => item.id === orderId);
      if (fresh) await loadTrucks(fresh);
    },
    [loadOrders, loadTrucks],
  );

  // --- Chiffres de la commande ouverte -------------------------------------

  // Le reste que le serveur accepte encore: sa lecture la plus fraîche (celle
  // de la préparation) d'abord, sinon celle de la liste.
  const toAllocate = Number(
    dispatchReadiness?.metrics?.remaining_liters ?? selectedProgress?.to_allocate_liters ?? 0,
  );
  const orderedLiters = Number(selectedProgress?.ordered_liters || 0);
  const allocatedLiters = Number(
    dispatchReadiness?.metrics?.allocated_liters ?? selectedProgress?.allocated_liters ?? 0,
  );
  const blockingReasons = dispatchReadiness?.blocking_reasons || [];
  const hardBlockers = blockingReasons.filter((reason) => reason.severity === 'error');

  const basketQuantity = availableTrucks.reduce(
    (sum, truck) => sum + (Number(quantities[truck.trip_id]) > 0 ? Number(quantities[truck.trip_id]) : 0),
    0,
  );
  const basketCount = availableTrucks.filter((truck) => Number(quantities[truck.trip_id]) > 0).length;
  const basketOverflow = Math.max(0, basketQuantity - toAllocate);
  const remainingAfterBasket = Math.max(0, toAllocate - basketQuantity);
  const availableCapacity = availableTrucks.reduce(
    (sum, truck) => sum + Number(truck.available_quantity || 0),
    0,
  );

  const blockReason = selectedOrder
    ? allocationBlockReason({
        toAllocate,
        truckCount: availableTrucks.length,
        blockers: dispatchReadiness ? blockingReasons : [],
      })
    : null;

  // --- Affichage des volumes ------------------------------------------------

  const cisternLabel = cisternLabelOf(volumeSettings);
  const cisternOf = (order) =>
    Number(order?.cistern_liters_snapshot) ||
    (order?.id === selectedId ? Number(dispatchReadiness?.metrics?.cistern_liters) : 0) ||
    cisternLitersOf(volumeSettings);
  const volumeCell = (liters, order) => {
    const value = Number(liters) || 0;
    if (displayUnit === UNIT_CISTERNS) {
      return formatNumber(Math.round((value / cisternOf(order)) * 100) / 100);
    }
    return nombreEntier(value);
  };

  // --- Actions ---------------------------------------------------------------

  const suggestedFor = (truck) => {
    const others = availableTrucks.reduce(
      (sum, other) =>
        other.trip_id === truck.trip_id
          ? sum
          : sum + (Number(quantities[other.trip_id]) > 0 ? Number(quantities[other.trip_id]) : 0),
      0,
    );
    return clampAllocation(
      truck.available_quantity,
      truck.available_quantity,
      Math.max(0, toAllocate - others),
    );
  };

  const handleAutoSplit = () => {
    if (!selectedOrder || availableTrucks.length === 0) {
      toast({
        title: 'Aucun camion libre',
        description: 'Il n’y a aucun camion à qui répartir cette commande.',
        variant: 'destructive',
      });
      return;
    }
    const { rows, uncovered } = suggestSplit(availableTrucks, toAllocate);
    setQuantities(
      Object.fromEntries(rows.map((row) => [row.trip_id, String(Math.round(row.quantity))])),
    );
    if (uncovered > 0) {
      toast({
        title: 'Les camions libres ne suffisent pas',
        description: `${nombreEntier(uncovered)} L resteront à affecter à un autre camion.`,
      });
    }
  };

  // Un seul appel pour un ou plusieurs camions: le serveur valide chaque ligne
  // contre le reste, dans une transaction, avec une clé de rejeu.
  const submitAllocations = async (rows) => {
    if (!selectedOrder || isSubmitting || rows.length === 0) return;
    const orderId = selectedOrder.id;
    setIsSubmitting(true);
    try {
      let result;
      try {
        result = await dispatchAPI.allocateBatch(orderId, rows);
      } catch (error) {
        toast({
          title: 'Affectation refusée',
          description: messageErreur(error, 'L’affectation n’a pas pu être enregistrée.'),
          variant: 'destructive',
        });
        // Le refus peut venir d'un état qui a bougé: un autre répartiteur a pris
        // le camion, la commande a été livrée. On relit plutôt que d'afficher
        // des chiffres périmés.
        await refreshAfterAllocation(orderId);
        return;
      }

      const rejected = result?.rejected || [];
      const applied = result?.applied || [];
      const refusedIds = new Set(rejected.map((item) => item.trip_id));
      // Ne reste saisi que ce qui n'est pas passé.
      setQuantities((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([tripId]) => refusedIds.has(tripId))),
      );

      if (rejected.length === 0) {
        const onlyRow = rows.length === 1 ? rows[0] : null;
        const truck = onlyRow && availableTrucks.find((item) => item.trip_id === onlyRow.trip_id);
        toast({
          title: applied.length > 1 ? 'Camions affectés' : 'Camion affecté',
          description: onlyRow
            ? `${nombreEntier(onlyRow.quantity)} L sur ${truck?.truck_matricule || 'le camion'}.`
            : `${applied.length} camion(s) affecté(s).`,
        });
      } else if (applied.length === 0) {
        toast({
          title: 'Affectation refusée',
          description: libre(rejected[0].message, 'Cette affectation a été refusée.'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Affectation faite en partie',
          variant: 'destructive',
          description: `${applied.length} camion(s) affecté(s), ${rejected.length} refusé(s) : ${libre(
            rejected[0].message,
            'affectation refusée',
          )}`,
        });
      }

      await refreshAfterAllocation(orderId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allocateSelection = () => {
    const rows = availableTrucks
      .filter((truck) => Number(quantities[truck.trip_id]) > 0)
      .map((truck) => ({
        trip_id: truck.trip_id,
        quantity: clampAllocation(quantities[truck.trip_id], truck.available_quantity, toAllocate),
      }))
      .filter((row) => row.quantity > 0);
    submitAllocations(rows);
  };

  // --- Rendu ----------------------------------------------------------------

  const changerVue = (valeur) => {
    setVue(valeur);
    const liste = valeur === VUE_A_AFFECTER ? activeOrders : openOrders;
    if (large && liste.length > 0 && !liste.some((order) => order.id === selectedId)) {
      handleSelectOrder(liste[0], { clavier: true });
    }
  };

  // Grand écran: le panneau s'ouvre d'office sur la première commande. Une
  // commande choisie par l'adresse (lien, rechargement) charge ses camions.
  useEffect(() => {
    if (isLoading || orders.length === 0) return;
    if (!selectedOrder) {
      if (large && shownOrders.length > 0) handleSelectOrder(shownOrders[0], { clavier: true });
      return;
    }
    if (chargePour.current !== selectedOrder.id) {
      chargePour.current = selectedOrder.id;
      loadTrucks(selectedOrder);
    }
  }, [isLoading, orders.length, selectedOrder, large, shownOrders, handleSelectOrder, loadTrucks]);

  const uniteCourte = displayUnit === UNIT_CISTERNS ? cisternLabel : 'L';

  const panneau = (afficherTitre) =>
    selectedOrder && selectedProgress ? (
      <DispatchPanel
        key={selectedOrder.id}
        afficherTitre={afficherTitre}
        order={selectedOrder}
        progress={selectedProgress}
        orderedLiters={orderedLiters}
        allocatedLiters={allocatedLiters}
        toAllocate={toAllocate}
        hardBlockers={hardBlockers}
        trucks={availableTrucks}
        trucksLoading={trucksLoading}
        quantities={quantities}
        setQuantities={setQuantities}
        isSubmitting={isSubmitting}
        blockReason={blockReason}
        basketQuantity={basketQuantity}
        basketCount={basketCount}
        basketOverflow={basketOverflow}
        remainingAfterBasket={remainingAfterBasket}
        availableCapacity={availableCapacity}
        cisternLiters={cisternOf(selectedOrder)}
        cisternLabel={cisternLabel}
        volumeCell={volumeCell}
        uniteCourte={uniteCourte}
        suggestedFor={suggestedFor}
        onAutoSplit={handleAutoSplit}
        onSubmit={allocateSelection}
        trajetCible={searchParams.get('trajet') || null}
        onVoirCommandes={() => navigate('/orders')}
        onVoirTrajets={() => navigate('/trips')}
      />
    ) : null;

  const renderOrdersCard = () => {
    // Le tourniquet n'apparaît qu'au premier chargement: après chaque
    // affectation la liste se relit, et la faire clignoter ferait perdre sa place.
    if (isLoading && orders.length === 0) {
      return (
        <div className="flex justify-center p-8">
          <span className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      );
    }
    if (loadError && orders.length === 0) {
      return (
        <EmptyState
          variant="error"
          title="Les commandes n’ont pas pu être chargées"
          description="Réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={loadOrders}
          className="border-0 py-8"
        />
      );
    }
    if (orders.length === 0) {
      return (
        <EmptyState
          icon={PackageCheck}
          title="Aucune commande à répartir"
          description="La répartition affecte les litres d’une commande aux camions qui sont partis charger. Elle commence par une commande client."
          actionLabel="Créer une commande"
          onAction={() => navigate('/orders')}
          className="border-0 py-8"
        />
      );
    }
    if (vueOrders.length === 0) {
      return vue === VUE_A_AFFECTER ? (
        <EmptyState
          icon={PackageCheck}
          title="Tout est déjà réparti"
          description="Chaque commande en cours a reçu tous ses camions. Les nouvelles commandes apparaîtront ici dès leur création."
          actionLabel={openOrders.length > 0 ? 'Voir les commandes déjà affectées' : undefined}
          onAction={() => changerVue(VUE_EN_COURS)}
          className="border-0 py-8"
        />
      ) : (
        <EmptyState
          icon={PackageCheck}
          title="Aucune commande en cours"
          description="Toutes les commandes sont livrées ou annulées."
          className="border-0 py-8"
        />
      );
    }
    return (
      <>
        <DispatchOrderList
          orders={pageCommandes.lignes}
          selectedId={selectedId}
          onSelect={handleSelectOrder}
          volumeCell={volumeCell}
          uniteCourte={uniteCourte}
          recherche={rechercheCommande}
          onRecherche={setRechercheCommande}
        />
        <BarrePagination pagination={pageCommandes} nom="commandes" />
      </>
    );
  };

  const boutonsBascule = (options, valeur, surChoix) => (
    <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          onClick={() => surChoix(v)}
          aria-pressed={valeur === v}
          className={cn(
            'h-7 rounded-md px-2.5 text-xs font-semibold transition-colors',
            valeur === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Exploitation"
        title="Répartition"
        description="Étape du parcours : la commande est passée, les camions sont partis charger. Affectez à chaque commande les camions qui ont du carburant à vendre. Ensuite : le départ des camions, puis la livraison."
        statsDisposition="ligne"
        stats={[
          {
            label: 'Commandes à affecter',
            valeur: activeOrders.length,
            ton: activeOrders.length > 0 ? 'attention' : 'positif',
            aide: 'Commandes qui attendent encore des camions',
            onClick: () => changerVue(VUE_A_AFFECTER),
          },
          {
            label: 'Litres à affecter',
            valeur: totalToAllocate,
            unite: 'L',
            ton: totalToAllocate > 0 ? 'attention' : 'positif',
            aide: 'Litres restant à affecter',
          },
          {
            label: 'Commandes en cours',
            valeur: openOrders.length,
            aide: 'Affectées, en route ou à affecter',
            onClick: () => changerVue(VUE_EN_COURS),
          },
        ]}
        actions={
          /* Répartir, c'est arbitrer entre ce qui roule et ce qui est libre:
             les deux inventaires s'impriment depuis cet écran. */
          <PrintMenu
            label="Imprimer"
            items={[
              {
                label: 'Camions en trajet',
                filename: 'camions-en-trajet',
                url: printingAPI.tripsInTransit,
                formats: ['pdf', 'xlsx', 'csv'],
              },
              {
                label: 'Parc par état',
                filename: 'parc-par-etat',
                url: printingAPI.trucksByStatus,
                formats: ['pdf', 'xlsx', 'csv'],
                params: { include_rented: true },
              },
            ]}
          />
        }
      />

      {/* Nos stations à réapprovisionner, à côté des commandes clients (lot 032). */}
      <StationsToResupply />

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,34rem)]">
        {/* La liste des commandes, une ligne compacte par commande. */}
        <section className="min-w-0 space-y-3 rounded-xl border border-border bg-card p-3 md:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-foreground">Commandes</h2>
            <div className="flex flex-wrap items-center gap-3">
              {boutonsBascule(
                [
                  [VUE_A_AFFECTER, `À affecter (${activeOrders.length})`],
                  [VUE_EN_COURS, `Toutes les commandes en cours (${openOrders.length})`],
                ],
                vue,
                changerVue,
              )}
              {/* Bascule de lecture seulement: rien de ce qui part au serveur ne
                  change d'unité. */}
              {boutonsBascule(
                [
                  [UNIT_LITERS, 'Litres'],
                  [UNIT_CISTERNS, 'Citernes'],
                ],
                displayUnit,
                setDisplayUnit,
              )}
            </div>
          </div>
          {renderOrdersCard()}
          {!selectedOrder && orders.length > 0 && !isLoading && !large && (
            <p className="text-center text-xs text-muted-foreground">
              Touchez une commande pour voir les camions libres.
            </p>
          )}
        </section>

        {/* Grand écran: panneau fixe, à hauteur de la fenêtre, défilement interne. */}
        {large && selectedOrder && selectedProgress && (
          <div ref={reperesPanneau} className="min-w-0 self-stretch">
            <aside
              aria-label="Détail de la commande"
              data-testid="dispatch-detail"
              style={hauteurPanneau ? { height: hauteurPanneau } : undefined}
              className="sticky top-20 h-[calc(100vh-10rem)] min-w-0 overflow-hidden rounded-xl border border-border bg-card"
            >
              {panneau(true)}
            </aside>
          </div>
        )}
      </div>

      {/* En dessous de 1280 px: le même contenu dans un volet. */}
      {!large && selectedOrder && selectedProgress && (
        <Sheet open={volet} onOpenChange={setVolet}>
          <SheetContent
            side={mobile ? 'bottom' : 'right'}
            className={cn(
              'flex flex-col gap-0 p-0',
              mobile ? 'h-[92dvh] rounded-t-2xl' : 'w-full max-w-xl sm:max-w-xl',
            )}
          >
            <div className="shrink-0 border-b border-border px-4 py-3 pr-12">
              <SheetTitle className="text-base">
                Commande {referenceLisible(selectedOrder.reference_number)}
              </SheetTitle>
              <SheetDescription className="sr-only">
                Affecter des camions libres à cette commande.
              </SheetDescription>
            </div>
            <div className="min-h-0 flex-1">{panneau(false)}</div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default DispatchPage;
